import { useEffect, useState } from 'react';
import { Trash2, PanelRightClose, PanelRightOpen, Plus, Braces } from 'lucide-react';
import { Alert } from '@dreamtree-org/twreact-ui';
import ModelSelect from '@/components/ModelSelect';
import { parseOptions } from '@/components/workflow/InputSchemaSection';
import { Field, VariableMenu, inputClass, textareaClass } from '@/components/workflow/inspectorFields';
import ToolArgumentsForm from '@/components/workflow/ToolArgumentsForm';
import TestStepPanel from '@/components/workflow/TestStepPanel';
import {
  HttpRequestConfig, SubWorkflowConfig, IteratorConfig, DelayConfig, TransformConfig, A2aCallConfig,
} from '@/components/workflow/FlowNodeConfigs';

/**
 * The per-node config panel.
 *
 * Lifted out of WorkflowDetail when the free-text provider/model/tool inputs were replaced
 * with real pickers fed from the live catalogs. Editors are dispatched through a registry
 * rather than a chain of ifs, and are deliberately hand-written rather than generated from
 * the catalog's `configFields`: a prompt wants a textarea, a mode wants a select, and a
 * generated form would render both as bare text inputs.
 *
 * The one place a form IS generated is a Tool node's ARGUMENTS (ToolArgumentsForm.jsx) —
 * and that is generated from the tool's own parameter schema, which actually declares
 * types, enums and requiredness, not from a flat list of field names.
 *
 * Each editor takes `{ d, set, opts }` — the node's data, a setter, and the fetched option
 * lists (providers, tools, connections, workflows, upstream node ids). Shared form
 * primitives live in inspectorFields.jsx so the flow-control editors can use the same ones.
 */

/** Every provider is listed, configured or not: a provider without a key is still a valid
 *  choice when the node borrows a Connection below, so disabling it would be wrong. The
 *  label carries the status instead. Disabled while a Connection is chosen — the run
 *  resolves the credential wholesale from the connection (services/chat/providers.js),
 *  so its provider is what actually runs and the free choice would be a lie. */
function ProviderSelect({ value, onChange, providers, disabled = false }) {
  return (
    <Field label="Provider" hint={disabled ? 'Derived from the chosen connection.' : undefined}>
      <select
        className={inputClass}
        value={value || 'openai'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {(providers.length ? providers : [{ key: 'openai', label: 'OpenAI', configured: true }]).map((p) => (
          <option key={p.key} value={p.key}>{p.label}{p.configured ? '' : ' — no key configured'}</option>
        ))}
      </select>
    </Field>
  );
}

/** Exposes config.connection_id, which services/workflows/nodes/index.js's callModel has
 *  always read but nothing in the UI could ever set. A connection carries the company's own
 *  API key and wins over the shared env key — wholesale, provider included, so `onPick`
 *  hands the resolved row (or null) over and the editor derives its provider from it. */
function ConnectionSelect({ value, onPick, connections }) {
  if (!connections.length) return null;
  return (
    <Field label="Connection" hint="Optional — use a company API key instead of the shared one. The model list and provider then follow this connection.">
      <select
        className={inputClass}
        value={value || ''}
        onChange={(e) => {
          const id = e.target.value ? Number(e.target.value) : null;
          onPick(id, connections.find((c) => c.id === id) || null);
        }}
      >
        <option value="">Default (shared key)</option>
        {connections.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>)}
      </select>
    </Field>
  );
}

/* ----------------------------------------------------------- output structure editor -- */

const OUTPUT_FIELD_TYPES = ['string', 'number', 'integer', 'boolean', 'enum', 'array'];
const OUTPUT_ITEM_TYPES = ['string', 'number', 'integer', 'boolean'];
const EMPTY_OUTPUT_FIELD = { name: '', type: 'string', description: '', optional: false };

/**
 * Parses a node's `output_schema` config (the JSON string the engine's parseOutputSchema
 * reads) into editor state. `advanced` marks a value the row editor cannot round-trip —
 * unparsable JSON, or object-typed fields with nested `properties` — so it is edited as
 * raw JSON instead of being silently rewritten.
 */
function parseOutputSchemaState(raw) {
  if (raw === undefined || raw === null || raw === '') return { fields: [], json: '', advanced: false };
  let parsed;
  try { parsed = JSON.parse(raw); } catch { return { fields: null, json: raw, advanced: true }; }
  const fields = Array.isArray(parsed) ? parsed : null;
  if (!fields) return { fields: null, json: raw, advanced: true };
  const advanced = fields.some(
    (f) => f && (f.type === 'object' || (f.type === 'array' && f.items && !OUTPUT_ITEM_TYPES.includes(f.items.type))),
  );
  return { fields, json: raw, advanced };
}

/** One row of the structured editor — mirrors InputSchemaSection's FieldRow, with the
 *  richer output types (integer/enum/array-of-scalar) the structured-output layer allows. */
function OutputFieldRow({ f, onUpdate, onRemove }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <input className={`${inputClass} flex-1`} placeholder="name" value={f.name || ''} onChange={(e) => onUpdate({ ...f, name: e.target.value })} />
        <select className={inputClass} value={f.type || 'string'} onChange={(e) => onUpdate({ ...f, type: e.target.value })}>
          {OUTPUT_FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className={`${inputClass} flex-1`} placeholder="description" value={f.description || ''} onChange={(e) => onUpdate({ ...f, description: e.target.value })} />
        <label className="flex items-center gap-1 text-xs text-secondary-500 whitespace-nowrap">
          <input type="checkbox" checked={!!f.optional} onChange={(e) => onUpdate({ ...f, optional: e.target.checked })} /> optional
        </label>
        <button type="button" onClick={onRemove} className="p-1 text-red-500 hover:bg-red-500/10 rounded" title="Remove field">
          <Trash2 size={14} />
        </button>
      </div>
      {f.type === 'enum' && (
        <input
          className={inputClass}
          placeholder="options (comma-separated — required for enum)"
          value={(f.options || []).join(', ')}
          onChange={(e) => onUpdate({ ...f, options: parseOptions(e.target.value) })}
        />
      )}
      {f.type === 'array' && (
        <select
          className={inputClass}
          value={(f.items && f.items.type) || 'string'}
          onChange={(e) => onUpdate({ ...f, items: { ...(f.items || {}), type: e.target.value } })}
        >
          {OUTPUT_ITEM_TYPES.map((t) => <option key={t} value={t}>array of {t}</option>)}
        </select>
      )}
    </div>
  );
}

/** The structured output the model must produce: a field list stored as the node's
 *  `output_schema` JSON — the same config an LLM App spec's `output` section compiles to
 *  and the engine enforces with the provider's native structured-output mode. A JSON
 *  escape hatch covers nested objects the row editor cannot express. */
function OutputSchemaEditor({ d, set }) {
  const initial = parseOutputSchemaState(d.output_schema);
  const [advanced, setAdvanced] = useState(initial.advanced);
  // Track the raw value so an outside change (undo, spec recompile reload) re-decides the
  // mode, while row edits — which round-trip through the same string — do not.
  const rawValue = d.output_schema ?? '';
  useEffect(() => { setAdvanced(parseOutputSchemaState(rawValue).advanced); }, [rawValue]);

  const write = (fields) => set('output_schema', fields.length ? JSON.stringify(fields) : null);
  let invalid = '';
  if (advanced && rawValue.trim()) {
    try { JSON.parse(rawValue); } catch { invalid = 'Not valid JSON yet.'; }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-secondary-500">Output structure</span>
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="flex items-center gap-1 text-[10px] text-primary-600"
        >
          <Braces size={11} />{advanced ? 'Field list' : 'JSON'}
        </button>
      </div>
      {advanced ? (
        <>
          <textarea
            className={textareaClass}
            rows={5}
            placeholder='[{"name":"answer","type":"string","description":"..."}]'
            value={d.output_schema || ''}
            onChange={(e) => set('output_schema', e.target.value || null)}
          />
          {invalid && <p className="text-xs text-amber-600">{invalid}</p>}
          <p className="text-[10px] text-secondary-500">
            Array of fields (<code>name, type, description, optional</code>); <code>object</code> fields
            take nested <code>properties</code>. Enforced on the model&apos;s answer.
          </p>
        </>
      ) : (
        <>
          {(initial.fields || []).map((f, i) => (
            <OutputFieldRow
              key={i}
              f={f}
              onUpdate={(next) => write(initial.fields.map((x, idx) => (idx === i ? next : x)))}
              onRemove={() => write(initial.fields.filter((_, idx) => idx !== i))}
            />
          ))}
          <button
            type="button"
            onClick={() => write([...(initial.fields || []), { ...EMPTY_OUTPUT_FIELD }])}
            className="flex items-center gap-1 text-xs text-primary-600"
          >
            <Plus size={14} /> Add field
          </button>
          <p className="text-[10px] text-secondary-500">
            The model&apos;s answer is forced into this structure — the node outputs it as an object,
            downstream as <code>{'{{node_id.output.field}}'}</code>. Empty = plain text.
          </p>
        </>
      )}
    </div>
  );
}

function LlmConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <ConnectionSelect
        value={d.connection_id}
        connections={opts.connections}
        onPick={(id, conn) => set('connection_id', id, conn ? { provider: conn.provider } : undefined)}
      />
      <ProviderSelect
        value={d.provider}
        onChange={(v) => set('provider', v)}
        providers={opts.providers}
        disabled={d.connection_id != null}
      />
      <Field label="Model">
        <ModelSelect value={d.model || ''} onChange={(v) => set('model', v)} provider={d.provider || 'openai'} connectionId={d.connection_id || null} />
      </Field>
      <Field label="System prompt">
        <textarea className={textareaClass} rows={3} value={d.system_prompt || ''} onChange={(e) => set('system_prompt', e.target.value)} />
      </Field>
      <Field label="Prompt">
        <textarea className={textareaClass} rows={6} value={d.prompt || ''} onChange={(e) => set('prompt', e.target.value)} />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('prompt', `${d.prompt || ''}${ref}`)} />
      <OutputSchemaEditor d={d} set={set} />
      <p className="text-xs text-secondary-500">
        Reference earlier nodes with <code>{'{{node_id.output}}'}</code>, and the run input with <code>{'{{input.field}}'}</code>.
      </p>
    </div>
  );
}

function ConditionConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <Field label="Mode">
        <select className={inputClass} value={d.mode || 'expression'} onChange={(e) => set('mode', e.target.value)}>
          <option value="expression">Expression (deterministic, free)</option>
          <option value="llm">LLM judged (costs a model call)</option>
        </select>
      </Field>
      {d.mode === 'llm' ? (
        <>
          <Field label="Question">
            <textarea className={textareaClass} rows={4} value={d.question || ''} onChange={(e) => set('question', e.target.value)} />
          </Field>
          <ProviderSelect value={d.provider} onChange={(v) => set('provider', v)} providers={opts.providers} />
          <Field label="Model">
            <ModelSelect value={d.model || ''} onChange={(v) => set('model', v)} provider={d.provider || 'openai'} />
          </Field>
        </>
      ) : (
        <>
          <Field label="Expression">
            <textarea className={textareaClass} rows={3} value={d.expression || ''} placeholder="input.score >= 5" onChange={(e) => set('expression', e.target.value)} />
          </Field>
          <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('expression', `${d.expression || ''}${ref}`)} />
          <p className="text-xs text-secondary-500">
            Comparisons (<code>== != &gt; &gt;= &lt; &lt;=</code>), <code>and</code>/<code>or</code>/<code>not</code>, and <code>contains</code>. No function calls.
          </p>
        </>
      )}
    </div>
  );
}

function ToolStatusNotice({ toolName, known, description }) {
  if (!toolName) return null;
  if (!known) {
    return (
      <p className="text-xs text-amber-600">
        <code>{toolName}</code> is not in this agent&apos;s toolset — the run will fail unless you change the
        agent or pick another tool.
      </p>
    );
  }
  return description ? <p className="text-[10px] text-secondary-500">{description}</p> : null;
}

/** The tool list is agent-scoped and auto-only (routes/workflowsRouter.js's /tool-options):
 *  inline calls refuse any other category mid-run, and a human checkpoint is an explicit
 *  Approval node, so offering a gated tool here would only produce a graph that saves
 *  and then fails. An optional per-node specialist overrides the workflow's agent for
 *  both the picker below and the run. */
function ToolConfig({ d, set, opts }) {
  const overrideKey = (d.agent_key || '').trim();
  const [overrideTools, setOverrideTools] = useState(null);
  useEffect(() => {
    if (!overrideKey || !opts.fetchAgentToolOptions) { setOverrideTools(null); return undefined; }
    let live = true;
    setOverrideTools(null);
    opts.fetchAgentToolOptions(overrideKey)
      .then((tools) => { if (live) setOverrideTools(tools); })
      .catch(() => { if (live) setOverrideTools([]); });
    return () => { live = false; };
  }, [overrideKey]);

  const options = overrideKey ? (overrideTools || opts.toolOptions) : opts.toolOptions;
  // A connector-scoped node (dropped from the palette's "Connectors" group) narrows its
  // Tool dropdown to that package's tools — d.connector is the connector key, and each
  // toolOption carries a `connector` tag from the /tool-options endpoint.
  const scoped = d.connector
    ? options.filter((t) => (t.connector || null) === d.connector)
    : options;
  const known = scoped.some((t) => t.name === d.tool_name);
  const toolDesc = scoped.find((t) => t.name === d.tool_name)?.description;

  return (
    <div className="space-y-3">
      <Field label="Agent (optional)" hint="Specialist whose toolset this node uses. Empty = the workflow's agent.">
        <select className={inputClass} value={d.agent_key || ''} onChange={(e) => set('agent_key', e.target.value)}>
          <option value="">Workflow default</option>
          {(opts.agents || []).map((a) => <option key={a.key} value={a.key}>{a.name} ({a.key})</option>)}
        </select>
      </Field>
      <Field label="Tool">
        <select className={inputClass} value={known ? d.tool_name : ''} onChange={(e) => set('tool_name', e.target.value)}>
          <option value="" disabled>{scoped.length ? 'Select a tool…' : 'No tools in this connector'}</option>
          {scoped.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
      </Field>
      <ToolStatusNotice toolName={d.tool_name} known={known} description={toolDesc} />
      <ToolArgumentsForm
        schema={scoped.find((t) => t.name === d.tool_name)?.parameters || null}
        value={d.arguments_json}
        onChange={(json) => set('arguments_json', json)}
        upstreamIds={opts.upstreamIds}
      />
    </div>
  );
}

/** One full specialist turn mid-graph: the named agent's persona, model policy and tool
 *  belt run on the prompt below, and its answer becomes this node's output. */
function AgentTurnConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <Field label="Specialist agent" hint="Required — this node never falls back to the workflow's agent.">
        <select className={inputClass} value={d.agent_key || ''} onChange={(e) => set('agent_key', e.target.value)}>
          <option value="" disabled>Select an agent…</option>
          {(opts.agents || []).map((a) => <option key={a.key} value={a.key}>{a.name} ({a.key})</option>)}
        </select>
      </Field>
      <Field label="Prompt">
        <textarea className={textareaClass} rows={6} value={d.prompt || ''} onChange={(e) => set('prompt', e.target.value)} />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('prompt', `${d.prompt || ''}${ref}`)} />
      <Field label="Max tool loops" hint="Ceiling on the specialist's model↔tool rounds (1–25, default 10).">
        <input
          className={inputClass}
          type="number"
          min={1}
          max={25}
          value={d.max_tool_loops ?? ''}
          placeholder="10"
          onChange={(e) => set('max_tool_loops', e.target.value === '' ? null : Number(e.target.value))}
        />
      </Field>
      <OutputSchemaEditor d={d} set={set} />
      <p className="text-xs text-secondary-500">
        Only <code>auto</code> tools run; gated ones are refused in-prose. Spend accrues to
        this agent&apos;s budget like one of its chat turns.
      </p>
    </div>
  );
}

/** Both fields are offered because the engine honours both: outputNodeValue() takes the
 *  rendered `template` if there is one, else the single value at `value_path`, else the
 *  whole scope. `value_path` had no editor at all, so an LLM-app compile (or a seed) could
 *  set it and nobody could see or change it afterwards. */
function OutputConfig({ d, set, opts }) {
  return (
    <div className="space-y-3">
      <Field label="Template (optional)" hint="A rendered string. Wins over the value path below.">
        <textarea className={textareaClass} rows={4} value={d.template || ''} onChange={(e) => set('template', e.target.value)} />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('template', `${d.template || ''}${ref}`)} />
      <Field label="Value path (optional)" hint="Return one node's value whole, e.g. normalize.output. A bare dot path, not a {{template}}.">
        <input
          className={inputClass}
          value={d.value_path || ''}
          placeholder="normalize.output"
          list="output-upstream-ids"
          onChange={(e) => set('value_path', e.target.value)}
        />
      </Field>
      <datalist id="output-upstream-ids">
        {(opts.upstreamIds || []).map((id) => <option key={id} value={`${id}.output`} />)}
      </datalist>
      <p className="text-xs text-secondary-500">Leave both empty to return every node&apos;s output.</p>
    </div>
  );
}

function ExitConfig({ d, set }) {
  return (
    <Field label="Reason">
      <textarea className={textareaClass} rows={3} value={d.reason || ''} onChange={(e) => set('reason', e.target.value)} />
    </Field>
  );
}

function InputConfig() {
  return (
    <p className="text-sm text-secondary-500">
      Where the run begins. Its output is the workflow input — define the fields it carries under
      “Input fields” below the canvas.
    </p>
  );
}

const IMAGE_SIZES = ['auto', '1024x1024', '1536x1024', '1024x1536', '1792x1024', '1024x1792'];
const IMAGE_QUALITIES = ['auto', 'low', 'medium', 'high', 'standard', 'hd'];

/** Text-to-image step: a prompt (templated from earlier nodes) rendered by the company's
 *  image provider, saved as a file. Its connection list is the ai-image category only —
 *  an LLM key would fail at the provider, so it is never offered here. */
function ImageGenConfig({ d, set, opts }) {
  const imageConnections = opts.imageConnections || [];
  return (
    <div className="space-y-3">
      <Field label="Prompt" hint="Describe the image. Reference earlier nodes with {{node_id.output}}.">
        <textarea className={textareaClass} rows={6} value={d.prompt || ''} onChange={(e) => set('prompt', e.target.value)} />
      </Field>
      <VariableMenu nodeIds={opts.upstreamIds} onInsert={(ref) => set('prompt', `${d.prompt || ''}${ref}`)} />
      <Field label="Model" hint="Optional — provider default (gpt-image-1).">
        <input className={inputClass} value={d.model || ''} onChange={(e) => set('model', e.target.value)} placeholder="gpt-image-1" />
      </Field>
      <Field label="Size">
        <select className={inputClass} value={d.size || 'auto'} onChange={(e) => set('size', e.target.value)}>
          {IMAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Quality">
        <select className={inputClass} value={d.quality || 'auto'} onChange={(e) => set('quality', e.target.value)}>
          {IMAGE_QUALITIES.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </Field>
      {imageConnections.length > 0 && (
        <Field label="Connection" hint="Optional — use a company API key instead of the shared one.">
          <select
            className={inputClass}
            value={d.connection_id || ''}
            onChange={(e) => set('connection_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Default (shared key)</option>
            {imageConnections.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>)}
          </select>
        </Field>
      )}
      <p className="text-xs text-secondary-500">
        Output: <code>{'{file_id, url, revised_prompt}'}</code> — reference it downstream as <code>{'{{node_id.output.url}}'}</code>.
      </p>
    </div>
  );
}

/**
 * Node type → its inspector editor. A type missing from here is SELECTABLE but not
 * configurable: `Editor` resolves to null and the panel shows only the Label field, which
 * is how a `transform` node could be dropped on the canvas and then offer no way to set the
 * function it runs. Kept level with lib/workflowNodeVisuals by a unit test
 * (NodeInspector.test.jsx), for the same reason the canvas registry is.
 */
export const CONFIG_EDITORS = {
  llm: LlmConfig, condition: ConditionConfig, tool: ToolConfig, agent_turn: AgentTurnConfig,
  output: OutputConfig, exit: ExitConfig, input: InputConfig,
  image_gen: ImageGenConfig, transform: TransformConfig,
  http_request: HttpRequestConfig, sub_workflow: SubWorkflowConfig,
  iterator: IteratorConfig, delay: DelayConfig, a2a_call: A2aCallConfig,
};

/** Node types engine.js's testNode can run on their own — the rest either have no behaviour
 *  outside a graph (input/output/exit) or PAUSE, which needs a real run row to park.
 *  Duplicated here rather than fetched: it decides whether one button renders, and the
 *  server rejects an untestable type anyway. */
const TESTABLE_TYPES = new Set(['llm', 'condition', 'tool', 'agent_turn', 'image_gen', 'http_request']);
function InspectorHeader({ node, onDelete, onToggleCollapsed }) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-[var(--app-border)]">
      {node ? (
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{node.data?.label || node.type}</div>
          <div className="text-[10px] text-secondary-500 font-mono truncate">{node.id}</div>
        </div>
      ) : <span className="text-sm font-semibold">Configuration</span>}
      <div className="flex items-center gap-1 shrink-0">
        {node && node.type !== 'input' && (
          <button type="button" onClick={onDelete} className="p-1 text-red-500 hover:bg-red-500/10 rounded" title="Delete node">
            <Trash2 size={14} />
          </button>
        )}
        <button type="button" onClick={onToggleCollapsed} title="Hide configuration" className="p-1 rounded text-secondary-500 hover:text-primary-600">
          <PanelRightClose size={14} />
        </button>
      </div>
    </div>
  );
}

function InspectorNodeContent({ node, onChangeData, opts, nodeError }) {
  const Editor = (node && CONFIG_EDITORS[node.type]) || null;
  return (
    <>
      {nodeError && <Alert variant="error">{nodeError}</Alert>}
      <Field label="Label">
        <input
          className={inputClass}
          value={node.data?.label || ''}
          onChange={(e) => onChangeData({ ...node.data, label: e.target.value })}
        />
      </Field>
      {Editor && (
        <Editor
          d={node.data || {}}
          set={(key, value, extra) => onChangeData({ ...node.data, [key]: value, ...(extra || {}) })}
          opts={opts}
        />
      )}
      {TESTABLE_TYPES.has(node.type) && (
        <TestStepPanel
          node={node}
          agentKey={(node.data && node.data.agent_key) || opts.workflowAgentKey}
          upstreamIds={opts.upstreamIds}
          runTestNode={opts.runTestNode}
        />
      )}
    </>
  );
}

export default function NodeInspector({
  node, onChangeData, onDelete, opts, collapsed, onToggleCollapsed, nodeError,
}) {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggleCollapsed}
        title="Show configuration"
        className="h-full w-9 shrink-0 flex items-start justify-center pt-3 rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] text-secondary-500 hover:text-primary-600"
      >
        <PanelRightOpen size={16} />
      </button>
    );
  }

  return (
    <div className="h-full w-80 shrink-0 flex flex-col rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] overflow-hidden">
      <InspectorHeader
        node={node}
        onDelete={onDelete}
        onToggleCollapsed={onToggleCollapsed}
      />

      <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5">
        {!node ? (
          <p className="text-sm text-secondary-500">
            Select a node to configure it, drag a component from the left onto the canvas, or drag between
            two handles to connect nodes.
          </p>
        ) : (
          <InspectorNodeContent
            node={node}
            onChangeData={onChangeData}
            opts={opts}
            nodeError={nodeError}
          />
        )}
      </div>
    </div>
  );
}
